import {PartnerVisits, PartnerData} from './PartnerVisits';
import Module from '@mamdev/cad.static-next-lib/js/base/module';
import {DateService} from '../../ts/services/date';
import {Tracker} from '../../ts/services/tracker';
import Tracking2 from '../../ts/services/tracking2';
import Viewport from '../../ts/services/viewport';

/**
 * Promotionbox module.
 * Shows a box where the user can visit an external partner.
 * Can work also as a layer which appears at the bottom of the screen.
 */
export default class Promotionbox extends Module {
  /**
   * .ppromotionbox__container element.
   */
  container: Element;

  /**
   * Init. from DOM.
   * @param {HTMLElement} root
   * @return {Promotionbox}
   */
  static attachTo(root: HTMLElement) {
    return new Promotionbox(root);
  }

  /**
   * Constructor.
   * @param {HTMLElement} root
   */
  constructor(root: HTMLElement) {
    super(root);

    this.name_ = 'promotionbox';
  }

  /**
   * Starts the module
   */
  start() {
    try {
      this.log('start');

      if (!this.root_.classList.contains('layer')) {
        Tracking2.bindTrackingExposeHandler(this.root_);
        return;
      }

      const partnerId = this.root_.dataset.partnerId;
      const forceLayer = this.root_.dataset.forceLayer;

      this.log('partnerId = ' + partnerId);
      this.log('forceLayer = ' + forceLayer);

      // If displaying the layer is enforced just show the layer.
      if (forceLayer) {
        this.initLayer();
        return;
      }

      if (!partnerId) return;

      const storedPartnerVisits = localStorage.getItem('partner-visits');

      // If no data is present, create a new object to store it.
      const partnerVisits = storedPartnerVisits ? JSON.parse(storedPartnerVisits) as PartnerVisits : new PartnerVisits();

      // If the user already saw a layer, 30 days must pass before a new layer can be shown.
      if (partnerVisits.lastLayerShown && DateService.daysElapsedSince(partnerVisits.lastLayerShown) < 30) return;

      // Each partner has a separate visit count and date.
      // If the user never visited this partner before, create a new object for it.
      partnerVisits.partners[partnerId] = partnerVisits.partners[partnerId] || new PartnerData();

      partnerVisits.partners[partnerId].visits += 1;

      // Show the layer if the user visited a page from the same partner twice in a week.
      if (DateService.daysElapsedSince(partnerVisits.partners[partnerId].lastVisit) <= 7) {
        if (partnerVisits.partners[partnerId].visits === 2) {
          // Two visits within a week, show layer and reset visit count
          this.initLayer();
          partnerVisits.partners[partnerId].visits = 0;
          partnerVisits.lastLayerShown = Date.now();
        }
      } else {
        // More than one week elapsed, count this visit as the first visit
        partnerVisits.partners[partnerId].visits = 1;
      }

      partnerVisits.partners[partnerId].lastVisit = Date.now();

      // Save new visit stats
      localStorage.setItem('partner-visits', JSON.stringify(partnerVisits));
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  /**
   * Prepares the layer to be shown and interacted with.
   */
  initLayer() {
    this.container = this.root_.getElementsByClassName('promotionbox__container')[0];

    if (!this.container) return;

    window.setTimeout(() => {
      this.setLayerHeight();
      this.showLayer();
      this.track('open');
    }, 1000);

    const closeButton = this.root_.getElementsByClassName('js-close')[0];

    if (closeButton) {
      closeButton.addEventListener('click', e => {
        e.preventDefault();
        this.track('close');
        this.hideLayer();
      });
    }

    const visitPartnerButton = this.root_.getElementsByClassName('js-visitpartner')[0];

    if (visitPartnerButton) {
      visitPartnerButton.addEventListener('click', () => {
        this.track('link');
        this.hideLayer();
      });
    }

    window.addEventListener('resize', () => this.setLayerHeight());
  }

  /**
   * Hides the layer.
   */
  hideLayer() {
    this.root_.classList.remove('show');
  }

  /**
   * Shows the layer.
   */
  showLayer() {
    this.root_.classList.add('show');
  }

  /**
   * Sets the correct height for the layer.
   */
  setLayerHeight() {
    let clientRectHeight = this.container.getBoundingClientRect().height;

    if (Viewport.isInAppView()) {
      clientRectHeight += 30;
    }

    this.root_.style.height = clientRectHeight + 'px';
  }

  /**
   * Track layer events.
   * @param {string} event Name of the event to track.
   */
  track(event: 'open' | 'close' | 'link') {
    let click3: string = event;

    if (event === 'link') click3 = 'textlink_1';

    Tracker.sendPixel('abolayer_' + event, {
      click1: window.ui.pageinfo.coremid,
      click2: 'abolayer',
      click3: click3,
      click4: 1
    });
  }
}
